import React, { lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { ReportsSource, useReportPdf, useStudyReports } from 'app/features/reports/view';
import useStudyReportActions, {
  UseStudyReportActionsArgs,
} from '../../hooks/useStudyReportActions';

// pdf.js is heavy: only download it once the reports are first opened.
const ReportPdfViewer = lazy(
  () => import('app/features/reports/view/components/ReportPdfViewer/ReportPdfViewer'),
);

// Covers the images viewer (which stays mounted underneath, toolbar included)
// but stays below MUI dialogs / drawers (1200+), so InfoManager opens on top
// of it, and above the side report's resize handle (1001).
const OVERLAY_Z_INDEX = 1002;

export interface ReportsOverlayProps extends Omit<UseStudyReportActionsArgs, 'report' | 'pdf'> {
  source: ReportsSource;
  /** SOPInstanceUID to show; the newest report when unset or not found. */
  selectedSop?: string;
  onSelect: (sopInstanceUid: string) => void;
  /** Phone layout: the actions collapse into one menu. */
  mobile: boolean;
}

/** The PDF report viewer over the images viewer (inside #ohif-viewer). */
const ReportsOverlay = ({
  source,
  selectedSop,
  onSelect,
  mobile,
  ...actionArgs
}: ReportsOverlayProps) => {
  const { reports, legacyLookupFailed, isLoading, isError, refetch } = useStudyReports(source);
  const selected = reports.find((r) => r.sopInstanceUid === selectedSop) ?? reports[0];
  const pdfQuery = useReportPdf(source, selected);

  const actions = useStudyReportActions({ ...actionArgs, report: selected, pdf: pdfQuery.data });

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: OVERLAY_Z_INDEX }}>
      <Suspense
        fallback={
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              height: '100%',
              bgcolor: '#000',
              color: '#fff',
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        }
      >
        <ReportPdfViewer
          reports={reports}
          isLoadingReports={isLoading}
          isReportsError={isError}
          onRetryReports={() => void refetch()}
          legacyLookupFailed={legacyLookupFailed}
          selected={selected}
          onSelect={onSelect}
          pdf={pdfQuery.data}
          isLoadingPdf={pdfQuery.isLoading}
          isPdfError={pdfQuery.isError}
          onRetryPdf={() => void pdfQuery.refetch()}
          actions={actions}
          compact={mobile}
        />
      </Suspense>
    </Box>
  );
};

export default ReportsOverlay;
